import { Link } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";

// Animated ellipsis component
const AnimatedEllipsis = () => (
  <span className="inline-flex items-center w-6">
    <span className="inline-block w-0 animate-ellipsis whitespace-nowrap overflow-hidden">...</span>
  </span>
);

// Add animation styles to the head
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes ellipsis {
      0% { width: 0; }
      100% { width: 1.5rem; }
    }
    .animate-ellipsis {
      animation: ellipsis 1.5s steps(4, end) infinite;
    }
  `;
  document.head.appendChild(style);
}

const WorkflowTaskCard = ({ task }) => {
  const [progressData, setProgressData] = useState({
    progress: 0,
    progress_message: "",
    status: task?.status || "",
  });
  const ws = useRef(null);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (task?.status?.toLowerCase() === "pending") {
      const wsUrl = `wss://1r9yo2hhm1.execute-api.us-west-2.amazonaws.com/production?id=${task.task_id}&type=task`;
      ws.current = new WebSocket(wsUrl);

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setProgressData({
            progress: data.progress || 0,
            progress_message: data.progress_message || "",
            status: data.status || "",
          });
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };

      ws.current.onerror = (error) => {
        console.error("WebSocket error:", error);
      };
    }

    return () => {
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [task?.task_id, task?.status]);

  const status = progressData.status.toLowerCase() || task?.status?.toLowerCase() || "pending";
  const isCompleted = status === "completed";
  const progress = progressData.progress || 0;

  return (
    <div className="w-full gap-y-2 rounded shadow-sm p-4 bg-white">
      <div className="w-full flex justify-between items-start">
        <div className="flex-1 min-w-0">
          <Link 
            to={`/v2/task/${task.task_id}`} 
            className="text-blue-500 hover:underline truncate block"
            title={task.task_id}
          >
            {task.task_id}
          </Link>
          <p className="text-sm text-gray-500 truncate" title={task.file}>
            {task.file?.split("/").pop()}
          </p>
          
          {/* Progress Bar */}
          {!isCompleted && (
            <div className="mt-2">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-1 flex items-center">
                <span>{progress}%</span>
                {progressData.progress_message ? (
                  <span> - {progressData.progress_message}</span>
                ) : (
                  <span className="flex items-center"> - Processing <AnimatedEllipsis /></span>
                )}
              </p>
            </div>
          )}
          
          {/* Status Badge */}
          <div className="mt-2">
            <span 
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                isCompleted 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-yellow-100 text-yellow-800'
              }`}
            >
              {status}
            </span>
          </div>
        </div>
        
        <div className="text-right">
          <p className="text-xs text-gray-400 whitespace-nowrap">
            {task?.created_at ? new Date(task.created_at).toLocaleTimeString() : ""}
          </p>
          <p className="text-xs text-gray-400">
            {task?.created_at ? new Date(task.created_at).toLocaleDateString() : ""}
          </p>
        </div>
      </div>
    </div>
  );
}

export default WorkflowTaskCard;
